import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import * as PIXI from "pixi.js";
import "leaflet-pixi-overlay";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { containerClasses, useTheme } from "@mui/material";
import { percentageNormalize } from "./utils/percentageNormalized";
import { useOutletContext } from "react-router-dom";
import Supercluster from "supercluster";

PIXI.settings.FAIL_IF_MAJOR_PERFORMANCE_CAVEAT = false;
PIXI.utils.skipHello();

const { Assets } = PIXI;

export default function PixiOverlay({
  markers = [],
  elementsONUS = [],
  enableClustering = true,
  onClustersChange,
  onClick,
  onBackgroundClick,
  onClusterClick,
  elementSelected,
  elementONUSelected,
}) {
  const { config } = useOutletContext();
  const MIN_ZOOM_CLUSTER = config?.escalas?.clusterFtth ?? 16;
  const maxClusterRadius = 38 * ((config?.escalas?.clusterArea ?? 5) / 2);
  const spriteByIdRef = useRef(new Map());

  const theme = useTheme();
  const colorMode = theme.palette.mode;

  /* ---------- STATE ---------- */
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [pixiOverlay, setPixiOverlay] = useState(null);
  const [clusters, setClusters] = useState([]);

  /* Índice do Supercluster e mapeamento id->marker */
  const scRef = useRef(null);
  const id2MarkerRef = useRef(new Map());

  /* ---------- MAP ---------- */
  const map = useMap();
  const zoom = useMemo(() => map?.getZoom() || 0, [map]);

  const textureKeys = useMemo(() => {
    const baseKeys = [
      ...new Set(markers.map((m) => m.iconId).filter(Boolean)),
    ].map((key) => `marker_${key}`);
    return [...baseKeys, "cluster_icon"];
  }, [markers]);

  /* ---------- BUILD: índice Supercluster (quando markers mudam) ---------- */
  useEffect(() => {
    if (!enableClustering || !markers.length) {
      scRef.current = null;
      id2MarkerRef.current = new Map();
      setClusters([]);
      return;
    }

    // Prepara GeoJSON + mapeamento id->marker (para recuperar o marker no ponto "single")
    const features = [];
    const id2Marker = new Map();

    for (const m of markers) {
      if (!(m?.position?.[0] && m?.position?.[1])) continue;
      const [lat, lng] = m.position;
      const fid = m.id ?? `${lat},${lng},${m.iconId ?? ""}`;
      id2Marker.set(fid, m);

      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lng, lat] }, // GeoJSON = [lng, lat]
        properties: {
          __id: fid,
          color: m.tint || "#ffffff",
        },
      });
    }

    // Cria o índice (maxZoom controla até que nível ainda clusteriza — usamos MIN_ZOOM_CLUSTER)
    const sc = new Supercluster({
      radius: Math.max(1, Math.round(maxClusterRadius)), // px
      maxZoom: Math.max(0, Math.floor(MIN_ZOOM_CLUSTER)), // clusteriza até esse zoom
      map: (props) => {
        // para cada ponto: histograma de cor (1 unidade)
        const color = props.color || "#ffffff";
        return { countByColor: { [color]: 1 }, total: 1 };
      },
      reduce: (acc, props) => {
        acc.countByColor = { ...(acc.countByColor || {}) };
        // agrega histogramas entre pontos do cluster
        for (const [color, n] of Object.entries(props.countByColor || {})) {
          acc.countByColor[color] = (acc.countByColor[color] || 0) + n;
        }
        acc.total = (acc.total || 0) + (props.total || 0);
      },
    });

    sc.load(features);
    scRef.current = sc;
    id2MarkerRef.current = id2Marker;
  }, [markers, enableClustering, maxClusterRadius, MIN_ZOOM_CLUSTER]);

  /* ---------- EFFECT: carrega texturas ---------- */
  useEffect(() => {
    if (!textureKeys.length) return;
    setTexturesLoaded(false);

    const promises = textureKeys.map((texKey) => {
      if (Assets.cache.has(texKey)) return Promise.resolve();

      // fallback para qualquer marcador
      Assets.add(texKey, getDefaultIcon());
      if (texKey === "cluster_icon") {
        Assets.add(texKey, getClusterIcon());
      }
      return Assets.load(texKey);
    });

    Promise.all(promises)
      .then(() => setTexturesLoaded(true))
      .catch((err) => console.error("Falha ao carregar texturas:", err));
  }, [textureKeys]);

  /* ---------- FUNÇÃO: atualiza clusters (viewport + zoom) ---------- */
  const updateClusters = useCallback(() => {
    if (!map) return setClusters([]);

    // Se não quiser agrupar OU zoom acima do corte => retorna "singles"
    if (
      !enableClustering ||
      map.getZoom() > MIN_ZOOM_CLUSTER ||
      !scRef.current
    ) {
      setClusters(
        markers.map((marker) => ({
          id: marker.id,
          center: marker.position,
          markers: [marker],
          colorGroups: { [marker.tint || "#ffffff"]: 1 },
          isSingle: true,
        }))
      );
      return;
    }

    // Pega somente o que está visível
    const b = map.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const z = Math.max(0, Math.round(map.getZoom()));

    const raw = scRef.current.getClusters(bbox, z);

    const next = raw.map((f) => {
      const [lng, lat] = f.geometry.coordinates;
      if (f.properties.cluster) {
        // Feature é um

        const count = f.properties.point_count || f.properties.total || 0;
        const colorGroups = f.properties.countByColor || {};
        return {
          id: `cluster_${f.id}`,
          center: [lat, lng],
          count,
          colorGroups,
          isSingle: false,
        };
      } else {
        // Feature é um ponto "single"
        const fid = f.properties.__id;
        const marker = id2MarkerRef.current.get(fid);
        return {
          id: marker?.id ?? fid,
          center: marker?.position ?? [lat, lng],
          markers: marker ? [marker] : [],
          colorGroups: { [marker?.tint || "#ffffff"]: 1 },
          isSingle: true,
        };
      }
    });

    setClusters(next);
  }, [map, enableClustering, MIN_ZOOM_CLUSTER, markers]);

  /* ---------- EFFECT: roda cluster ao iniciar e quando mover/zoom ---------- */
  useEffect(() => {
    if (!map) return;

    updateClusters();
    const handler = () => updateClusters();

    // moveend/zoomend => event único por gesto (econômico)
    map.on("moveend zoomend", handler);
    return () => {
      map.off("moveend zoomend", handler);
    };
  }, [map, updateClusters]);

  /* ---------- EFFECT: cria overlay uma única vez ---------- */
  useEffect(() => {
    if (!map) return;

    const container = new PIXI.Container();
    const overlay = L.pixiOverlay(
      (utils) => {
        const scale = utils.getScale();
        const renderer = utils.getRenderer();
        const container = utils.getContainer();
        renderer.reset();

        // Tamanho dinâmico baseado no zoom
        container.children.forEach((c) => {
          const RADIUS_PX = c.isCluster
            ? 0.4 + (parseFloat(config?.escalas?.clusterIconSize) || 5) / 20
            : 0.3 + (parseFloat(config?.escalas?.onuIcon) || 5) / 20;
          const invScale = 1 / scale;
          c.scale.set(RADIUS_PX * invScale);
        });

        renderer.render(container);
      },
      container,
      { destroyInteractionManager: false }
    );

    overlay.addTo(map);
    setPixiOverlay(overlay);
    return () => overlay.remove();
  }, [map, config]);

  /* ---------- EFFECT: desenha sprites ---------- */
  useEffect(() => {
    if (!pixiOverlay || !texturesLoaded) return;

    const { utils } = pixiOverlay;
    const container = utils.getContainer();
    const project = utils.latLngToLayerPoint;
    const scale = utils.getScale();

    container.removeChildren();

    drawBackground(container);

    if (enableClustering && map.getZoom() < 18) {
      // Desenhar clusters + singles
      clusters.forEach((cluster) => {
        if (cluster.isSingle) {
          const marker = cluster.markers[0];
          if (marker) drawMarker(marker, container, project, scale);
        } else {
          drawCluster(cluster, container, project);
        }
      });
    } else {
      markers.forEach((marker) =>
        drawMarker(marker, container, project, scale)
      );
    }

    if (elementsONUS) {
      elementsONUS.forEach((el) => drawMarker(el, container, project, scale));
    }

    pixiOverlay.redraw();
  }, [
    pixiOverlay,
    texturesLoaded,
    colorMode,
    markers,
    elementsONUS,
    clusters,
    enableClustering,
    map,
  ]);

  /* ---------- HELPERS DE DESENHO ---------- */
  const drawBackground = (container) => {
    const bg = new PIXI.Graphics();
    const size = 1e10;
    const hitArea = new PIXI.Rectangle(-size, -size, size * 2, size * 2);
    bg.hitArea = hitArea;
    bg.interactive = true;
    bg.eventMode = "static";

    let pointerDownTime = 0;
    const CLICK_THRESHOLD = 250; // ms

    bg.on("pointerdown", () => {
      pointerDownTime = Date.now();
    });

    bg.on("pointerup", (e) => {
      const duration = Date.now() - pointerDownTime;
      if (duration < CLICK_THRESHOLD) {
        e.stopPropagation();
        if (typeof onBackgroundClick === "function") {
          onBackgroundClick();
        }
      }
    });

    container.addChild(bg);
  };

  const drawMarker = (m, container, project, scale) => {
    const texture = Assets.get(`marker_${m.iconId}`);
    const markerTexture = Assets.get(`${m.baseIcon}`);
    if (!(texture && markerTexture)) return;

    if (!(m?.position?.[0] && m?.position?.[1])) return;
    const point = project(L.latLng(...m?.position));

    const node = new PIXI.Container();
    node.position.set(point.x, point.y);
    node.markerId = m.id;

    const spriteMarker = new PIXI.Sprite(markerTexture);
    spriteMarker.tint = m.tint ? PIXI.utils.string2hex(m.tint) : 0xffffff;
    spriteMarker.anchor.set(0.5);

    const sprite = new PIXI.Sprite(texture);
    sprite.tint = colorMode === "light" ? 0x000000 : 0xffffff;
    sprite.anchor.set(0.5);

    node.addChild(spriteMarker);
    node.addChild(sprite);

    node.eventMode = "static";
    node.cursor = "pointer";

    node.on("pointerdown", (e) => {
      e.stopPropagation();
      onClick(m.id);
    });

    container.addChild(node);
    // Guarda referência dos sprites
    spriteByIdRef.current.set(m.id, { spriteMarker, sprite });
  };

  const drawCluster = (cluster, container, project) => {
    if (!(cluster?.center?.[0] && cluster?.center?.[1])) return;

    const point = project(L.latLng(...cluster.center));
    const node = new PIXI.Container();
    node.position.set(point.x, point.y);
    node.isCluster = true;
    node.interactive = true;
    node.buttonMode = true;
    node.markerId = cluster.id;
    node.clusterData = cluster;

    // Fundo
    const bgCircle = new PIXI.Graphics();
    bgCircle.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 0.8);
    bgCircle.drawCircle(0, 0, 2);
    bgCircle.endFill();

    // Donut por cor
    const colors = Object.entries(cluster.colorGroups || {});
    const normalizedColors = percentageNormalize(colors, 0.075);

    if (normalizedColors.length > 1) {
      drawPieChart(node, normalizedColors, colorMode);
    } else if (normalizedColors.length === 1) {
      const color = normalizedColors[0][0];
      const donut = new PIXI.Graphics();
      donut.beginFill(PIXI.utils.string2hex(color), 1);
      donut.drawCircle(0, 0, 16 * 2);
      donut.endFill();

      donut.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 1);
      donut.drawCircle(0, 0, 12 * 2);
      donut.endFill();

      node.addChild(donut);
    }

    const total =
      (typeof cluster.count === "number" && cluster.count) ||
      (Array.isArray(cluster.markers) ? cluster.markers.length : 0) ||
      0;

    const formatCount = (n) => {
      if (n >= 10000) {
        const rounded = Math.round((n / 1000) * 10) / 10;
        return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded) + "k";
      }
      return n.toLocaleString();
    };

    const countText = new PIXI.Text(formatCount(total), {
      fontFamily: "Arial",
      fontSize: 9 * 2,
      fontWeight: "bold",
      fill: colorMode === "light" ? 0x000000 : 0xffffff,
      align: "center",
    });
    countText.anchor.set(0.5);

    node.addChild(bgCircle);
    node.addChild(countText);

    node.on("click", (e) => {
      e.stopPropagation();
      onClusterClick(cluster);
    });

    container.addChild(node);
  };

  const drawPieChart = (container, colors, colorMode) => {
    const donut = new PIXI.Graphics();
    let startAngle = -Math.PI / 2;

    // Maior fatia primeiro (melhor rende)
    colors.sort((a, b) => b[1] - a[1]);

    colors.forEach(([color, percentage]) => {
      const sliceAngle = percentage * 2 * Math.PI;
      donut.beginFill(PIXI.utils.string2hex(color), 1);
      donut.moveTo(0, 0);
      donut.arc(0, 0, 16 * 2, startAngle, startAngle + sliceAngle);
      donut.lineTo(0, 0);
      donut.endFill();
      startAngle += sliceAngle;
    });

    donut.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 1);
    donut.drawCircle(0, 0, 12 * 2);
    donut.endFill();

    container.addChild(donut);
  };
  useEffect(() => {
    if (!pixiOverlay) return;

    const ticker = PIXI.Ticker.shared;

    const animate = () => {
      const ids = Array.from(
        new Set([elementSelected?.id, elementONUSelected?.id].filter(Boolean))
      );
      if (ids.length === 0) return;

      let touched = false;

      ids.forEach((key) => {
        const sprites = spriteByIdRef.current.get(key);
        if (!sprites) return;

        const { sprite, spriteMarker } = sprites;
        if (!sprite || !spriteMarker) return;
        sprite.scale.set(1 + 0.16 * Math.sin(ticker.lastTime / 300));
        spriteMarker.scale.set(1 + 0.16 * Math.sin(ticker.lastTime / 300));

        touched = true;
      });

      if (touched) pixiOverlay.redraw();
    };

    ticker.add(animate);
    return () => ticker.remove(animate);
  }, [pixiOverlay, elementSelected?.id, elementONUSelected?.id]);

  /* ---------- EFFECT: expor clusters para o componente pai ---------- */
  useEffect(() => {
    if (onClustersChange) {
      onClustersChange(clusters);
    }
  }, [clusters, onClustersChange]);

  return null;
}

/* ---------- fallback: ícone SVG azul ---------- */
function getDefaultIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
<path d="M12.8 32V20.7059H19.2V32H27.2V16.9412H32L16 0L0 16.9412H4.8V32H12.8Z" fill="white"/>
</svg>`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}

/* ---------- cluster icon ---------- */
function getClusterIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="20" fill="white"/>
  </svg>`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}
